import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import { useBranding } from '../lib/branding';

const Section = ({ n, title, children }) => (
  <section className="mt-6" data-testid={`terms-section-${n}`}>
    <h2 className="font-display text-[16px] leading-tight">
      <span className="font-mono text-neutral-400 mr-2">{String(n).padStart(2, '0')}</span>
      {title}
    </h2>
    <div className="mt-2 text-[13px] leading-relaxed text-neutral-700 space-y-2">
      {children}
    </div>
  </section>
);

const Terms = () => {
  const { branding } = useBranding();
  const brand = (branding.logo_text || 'PrintingKit');
  const company = (branding.powered_by || 'MACFINTI SOLUTIONS PVT LTD');
  const contactPhone = branding.contact_phone || '+91 93640 19465';
  const supportEmail = 'support@printingkit.store';
  const updated = 'February 2026';

  return (
    <div className="p-6 pb-16" data-testid="terms-page">
      <Link to="/" className="text-[10px] tracking-[0.24em] uppercase font-bold inline-flex items-center gap-1" data-testid="terms-back">
        <ArrowLeft size={12} /> Back to home
      </Link>

      <div className="mt-3 reg-mark">
        <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— Legal</div>
        <h1 className="font-display text-[30px] mt-2 leading-tight">Terms of Service</h1>
        <p className="mt-2 text-[12px] text-neutral-500 font-mono">Last updated: {updated}</p>
      </div>

      <p className="mt-5 text-[13px] leading-relaxed text-neutral-700">
        These Terms of Service ("Terms") govern your access to and use of the <b>{brand}</b> website
        (<a className="underline" href="https://printingkit.store">printingkit.store</a>) and mobile
        application (collectively, the "Service"), operated by <b>{company}</b> ("we", "us", "our").
        By creating an account, placing an order, or otherwise using the Service you agree to be
        bound by these Terms. If you do not agree, please do not use the Service.
      </p>

      <Section n={1} title="Eligibility & accounts">
        <p>You must be at least 18 years old (or the age of majority in your jurisdiction) and legally capable of entering into a binding contract in India to use the Service. You are responsible for keeping your login credentials confidential and for all activity that occurs under your account.</p>
        <p>You agree to provide accurate, current and complete registration details (name, phone, address, GST if applicable) and to update them promptly if they change. We may suspend or terminate an account that contains false or misleading information.</p>
      </Section>

      <Section n={2} title="The Service">
        <p><b>{brand}</b> is a print-on-demand marketplace that connects customers with independent local print partners. We facilitate order placement, payment, artwork transfer, chat and delivery co-ordination. Print partners are independent businesses — not our employees — and are solely responsible for the quality and timeliness of the print job they accept.</p>
      </Section>

      <Section n={3} title="Placing an order">
        <ol className="list-decimal pl-5 space-y-1">
          <li>You browse our catalog, configure a product (material, size, quantity, finish) and upload your artwork.</li>
          <li>You review the price breakdown and any applicable coupon discount.</li>
          <li>You confirm your delivery address, phone number and any special instructions.</li>
          <li>You pay the total amount online. Payment must clear before your order is broadcast to print partners.</li>
          <li>The first available approved print partner accepts your order and begins production.</li>
        </ol>
        <p>A confirmed order forms a binding contract between you and the assigned print partner for the printing services described in the order summary.</p>
      </Section>

      <Section n={4} title="Payments, pricing & taxes">
        <p>All prices are shown in Indian Rupees (INR) and are inclusive of GST unless stated otherwise. Payments are processed by our PCI-DSS compliant gateway (Razorpay). We do not store your card, UPI or bank credentials.</p>
        <p>We may correct pricing errors, decline or cancel orders, and adjust promotional discounts (including coupons) at our discretion. Where an order is cancelled due to a pricing error before print, you will receive a full refund.</p>
      </Section>

      <Section n={5} title="Artwork, files & content responsibility">
        <p>By uploading a file you confirm that:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>You own or have obtained all necessary rights, licences and consents (including trademark, copyright and personality rights) to reproduce the artwork.</li>
          <li>The content does not infringe any third-party intellectual property, is not defamatory, obscene, hateful, or otherwise unlawful under Indian law.</li>
          <li>You retain full ownership of your artwork. You grant us and the assigned print partner a limited, revocable licence to reproduce the file solely to fulfil your order.</li>
        </ul>
        <p>We reserve the right (but have no obligation) to refuse or cancel any order whose content, in our reasonable judgement, violates these Terms or applicable law.</p>
      </Section>

      <Section n={6} title="Delivery, delays & risk of loss">
        <p>Estimated delivery windows are indicative and depend on the partner's workload, product complexity and your pincode. Title and risk of loss pass to you once the item is handed over at the delivery address. If your order is materially delayed you may contact support for a resolution (partner reassignment, partial refund or cancellation).</p>
      </Section>

      <Section n={7} title="Cancellation, returns & refunds">
        <p><b>Before printing:</b> you may cancel and receive a full refund (minus any payment-gateway fees) up until a print partner marks the order as <i>Printing</i>.</p>
        <p><b>After printing has started:</b> cancellations are not possible because your artwork has been committed to physical media at your specification.</p>
        <p><b>Defective print:</b> if the delivered item has a manufacturing defect (misprint, wrong material, damaged packaging) tap "I Received It" is <b>not</b> the correct action. Instead open the order chat within 48 hours of delivery, share photos, and our support team will co-ordinate a free reprint or refund at our discretion.</p>
        <p>Approved refunds are credited to the original payment method within 5–10 business days.</p>
      </Section>

      <Section n={8} title="Acceptable use">
        <p>You agree not to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Upload content that is illegal, hateful, obscene, threatening, or infringes any third-party right.</li>
          <li>Reverse-engineer, decompile, scrape or interfere with the Service or its servers.</li>
          <li>Impersonate another person or misrepresent your affiliation with any entity.</li>
          <li>Use the Service to send unsolicited marketing or spam.</li>
          <li>Attempt to defraud, phish or manipulate our print partners.</li>
        </ul>
        <p>We may suspend or permanently ban accounts that violate this section, with or without notice.</p>
      </Section>

      <Section n={9} title="Print partner obligations">
        <p>If you are registered as a print partner, you additionally agree to keep your machine capabilities and pricing accurate, to accept jobs only within your declared capacity, to complete accepted orders within the committed timeline, and to maintain professional conduct in the order chat. Repeated rejections, no-shows or low customer ratings may result in de-listing.</p>
      </Section>

      <Section n={10} title="Intellectual property">
        <p>The Service — including its design, catalogue, code, brand marks and product photography — is owned by <b>{company}</b> and protected by Indian and international copyright, trademark and other laws. You may not copy, reproduce, modify or create derivative works from the Service without prior written permission.</p>
      </Section>

      <Section n={11} title="Disclaimers">
        <p>The Service is provided on an "as is" and "as available" basis. To the maximum extent permitted by law we disclaim all warranties, express or implied, including merchantability, fitness for a particular purpose and non-infringement. We do not warrant that the Service will be uninterrupted, error-free, or that colours on your screen will match the printed output exactly.</p>
      </Section>

      <Section n={12} title="Limitation of liability">
        <p>To the maximum extent permitted by applicable law, our aggregate liability for any claim arising out of or relating to the Service shall not exceed the total amount you paid us for the specific order that gave rise to the claim. We are not liable for any indirect, incidental, special, consequential or punitive damages, or for lost profits, revenues, data or goodwill.</p>
      </Section>

      <Section n={13} title="Indemnity">
        <p>You agree to indemnify and hold harmless {company}, its officers, employees and print partners from any claim, demand, loss, liability or expense (including reasonable legal fees) arising out of (a) your artwork or content, (b) your breach of these Terms, or (c) your violation of any law or third-party right.</p>
      </Section>

      <Section n={14} title="Governing law & disputes">
        <p>These Terms are governed by the laws of the Republic of India. Any dispute arising out of or relating to the Service shall be subject to the exclusive jurisdiction of the competent courts in Bengaluru, Karnataka. Before filing any court action you agree to first contact us at <a className="underline" href={`mailto:${supportEmail}`}>{supportEmail}</a> and give us 30 days to resolve the matter amicably.</p>
      </Section>

      <Section n={15} title="Changes to these terms">
        <p>We may revise these Terms from time to time. Material changes will be announced in-app and by email at least 14 days before they take effect. Continued use of the Service after the effective date constitutes acceptance of the revised Terms. The "Last updated" date at the top of this page reflects the latest revision.</p>
      </Section>

      <Section n={16} title="Termination">
        <p>You may stop using the Service and delete your account at any time from your Profile page. We may suspend or terminate your access if you breach these Terms, if required by law, or if we discontinue the Service. Provisions that by their nature should survive termination (payment, IP, indemnity, liability, governing law) shall survive.</p>
      </Section>

      <Section n={17} title="Contact us">
        <p><b>Grievance Officer:</b> Customer Support Lead, {company}</p>
        <p><b>Email:</b> <a className="underline" href={`mailto:${supportEmail}`}>{supportEmail}</a></p>
        <p><b>Phone:</b> <a className="underline font-mono" href={`tel:${contactPhone.replace(/\s+/g, '')}`}>{contactPhone}</a></p>
        <p><b>Postal:</b> {company}, India</p>
      </Section>

      <div className="mt-8 text-[12px] text-neutral-600">
        See also our <Link to="/privacy" className="underline font-bold" data-testid="terms-privacy-link">Privacy Policy</Link>.
      </div>

      <div className="mt-10 text-center text-[10px] tracking-[0.22em] uppercase text-neutral-400">
        © 2026 {brand} · All rights reserved
      </div>
    </div>
  );
};

export default Terms;
